import { Component, ElementRef, EventEmitter, Injector, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { PostService } from '../../../service/post-service/post.service';
import { AuthService } from '../../../service/auth-service/auth-service.service';
import { Art57Section } from '../../../ui/Art57Section/Art57Section';
import { LoggerService } from '../../../service/logger-service/logger.service';
import { environment } from '../../../../../environments/environment';
import { PostSection } from '../../../ui/Art57Section/PostSection';
import { ConfirmationService } from 'primeng/api';
import { ValidationResult } from '../../../ui/Data/PostData/ValidationResult';
import { ValidateOptions } from '../../../ui/Data/PostData/ValidateOptions';
import { Util } from '../../../ui/Util';

@Component({
  selector: 'app-detailed-post-view',
  templateUrl: './detailed-post-view.component.html',
  styleUrls: ['./detailed-post-view.component.scss']
})
export class DetailedPostViewComponent implements OnInit, OnDestroy {
  @Output() buttonAction = new EventEmitter<any>();

  @ViewChild('fileInput', { static: false }) fileInput!: ElementRef<HTMLInputElement>;

  private art57Section: Art57Section;
  private postSection: PostSection;
  private revokeTimeoutId: any = null;
  public isDragOver = false;
  public maxZipFileByteSize = environment.maxZipFileByteSizeEvpost;
  public postLoading$ = this.injector.get(PostService).loading; // Expose loading observable for template via async pipe
  
  constructor(
    private injector: Injector,
    public authService: AuthService,
    private logger: LoggerService,
    private confirmationService: ConfirmationService,
    private postService: PostService
  ) {
    this.art57Section = this.injector.get(Art57Section);
    this.postSection = this.art57Section.post;
  }

  ngOnInit(): void {
    this.initComponent();
  }

  ngOnDestroy(): void {
    this.postSection.revokeObjectUrl();
    if (this.revokeTimeoutId) {
      clearTimeout(this.revokeTimeoutId);
      this.revokeTimeoutId = null;
    }
  }

  initComponent(): void {
    this.resetFileInput(); // keep input in sync if no file
  }

  resetFileInput(): void {
    if (this.fileInput && this.fileInput.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }

  /* getters / setters that proxy to PostSection */
  get selectedFile(): File | null { return this.postSection.selectedFile; }
  get fileContent(): string { return this.postSection.fileContent; }
  set fileContent(val: string) { this.postSection.setFileContent(val); }
  get validationResult(): ValidationResult { return this.postSection.validationResult; }
  set validationResult(v: ValidationResult) { this.postSection.setValidation( { ok: v.ok, message: this.postSection.validationResult.message, title: this.postSection.validationResult.title } ); }

  /* Drag & drop handlers */
  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    if (!event.dataTransfer) { return; }
    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      this.handleDroppedFiles(files);
    }
  }

  /* Drag & drop */
  handleDroppedFiles(fileList: FileList): void {
    if (!fileList || fileList.length === 0) { return; }

    const lastIndex = fileList.length - 1;
    const file = fileList.item(lastIndex);
    if (!file) { return; }

    let chosenFile: File | null = null;

    if (fileList.length > 1) {
      // Look for the last ZIP by scanning from the end
      for (let i = fileList.length - 1; i >= 0; i--) {
        const f = fileList.item(i);
        if (!f) { continue; }
        if (f.name.toLowerCase().endsWith('.zip')) {
          chosenFile = f;
          break;
        }
      }

      // If no ZIP found, take the last file in the list by default
      if (!chosenFile) {
        chosenFile = fileList.item(fileList.length - 1);
      }
      this.art57Section.showWarningTooltipMessage('Drag and drop of multiple ZIP files is not supported. Only the last selected ZIP file will be uploaded');
    } else {
      chosenFile = file;
    }

    this.art57Section.showSuccessTooltipMessage('File was uploaded');
    this.postSection.setSelectedFile(chosenFile);
    this.validate({ file: this.selectedFile });
  }

  /* Drag & drop */
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.isDragOver) { this.isDragOver = true; }
    if (event.dataTransfer) { event.dataTransfer.dropEffect = 'copy'; }
  }

  /* Drag & drop */
  onDragEnter(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  /* Drag & drop */
  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  /* Drag & drop */
  onDropzoneKeydown(event: KeyboardEvent): void {
    const key = event.key;
    if (key === 'Enter' || key === ' ') {
      event.preventDefault();
      this.triggerFilePicker();
    }
  }

  /* Drag & drop */
  triggerFilePicker(): void {
    if (this.fileInput && this.fileInput.nativeElement) {
      this.fileInput.nativeElement.click();
    }
  }

  /* Drag & drop */
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length) {
      this.handleDroppedFiles(input.files);
    } else {
      this.postSection.resetSection();
    }
  }

  /* Drag & drop */
  onFileContentChange(content: string) { this.fileContent = content; }

  /* Open file in new window for viewing */
  openFileInNewWindow(): void {
    if (!this.postSection.selectedFile) { return; }
    const objectUrl = this.postSection.createObjectUrlForSelectedFile();
    if (!objectUrl) { return; }

    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = this.postSection.selectedFile.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // revoke after delay and clear stored objectUrl
    this.revokeTimeoutId = window.setTimeout(() => {
      this.postSection.revokeObjectUrl();
      this.revokeTimeoutId = null;
    }, 10000);

    this.art57Section.showSuccessTooltipMessage('Please see the viewed file in the Browser download history');
  }

  async onSend(): Promise<void> {
    this.validate( { file: this.selectedFile, validateOnServer: false  } ); // skip server validation, it will be validated by the submit REST below on the server side
    
    if (!this.validationResult.ok) { 
      return; 
    }
    const file: File = this.selectedFile as File;

    this.postService.submit(file).subscribe({
      next: (message: any) => {        
        // message is the server JSON (may include file (base64), filename, errorMessage, errorCode, etc.)
        this.fileContent = message?.filename ?? this.fileContent;

        if (message && message.errorMessage) {
          // Server returned a validation/error -> set into section and show error
          const title = this.buildErrorTitle(message);
          const fullMessage = this.buildErrorMessage(message);
          this.postSection.setValidation( {ok: false, message: fullMessage, title: title} );
          this.art57Section.showErrorTooltipMessage( this.postSection.validationResult.message, this.postSection.validationResult.title );
          return;
        }

        if (message && message.file && message.filename) {
          // Success: create blob, show confirmation dialog to download
          this.art57Section.showSuccessTooltipMessage('Validated successfully');
          const link = document.createElement('a');
          const blob = this.createBlob(message.file);
          const url: string = this.createDownloadLink(blob);
          this.setDownloadLink(link, url, message.filename);
          this.art57Section.showSuccessTooltipMessage('File Transfer Complete');
          this.showDialogTransferCompleted(url, message, link);

          // Optionally reset section on success
          this.postSection.resetSection();
        } else {
          this.art57Section.showErrorTooltipMessage('Unexpected response from the server');
        }
      },
      error: (err) => {
        this.art57Section.showErrorTooltipMessage(`Error response from the server when processing the submitted file (${err.status || 'Unknown error'})`);
      }
    });
  }

  /* Server Side validation */
  async onValidateServerSide(): Promise<void> {
    const file: File = this.selectedFile as File;
    this.postService.validate(file).subscribe({
      next: (message: any) => {
        if (message && message.errorMessage) {
          const title = this.buildErrorTitle(message);
          const fullMessage = this.buildErrorMessage(message);
          this.postSection.setValidation( {ok: false, message: fullMessage, title: title} );
          this.art57Section.showErrorTooltipMessage( this.postSection.validationResult.message, this.postSection.validationResult.title );
        } else if (message && message.file && message.filename) {
          this.art57Section.showSuccessTooltipMessage('Validated successfully');
        } else {
          this.art57Section.showErrorTooltipMessage('Unexpected response from the server');
        }
      },
      error: (err) => {
        this.art57Section.showErrorTooltipMessage(`Error response from the server when validating the submitted file (${err.status || 'Unknown error'})`);
      }
    });
  }

  /* Validation client + server side*/
  private validate(validateOptions?: ValidateOptions) {
    const defaults: ValidateOptions = { file: undefined, validateOnServer: true };
    const vldOptions = { ...defaults, ...(validateOptions ?? {}) };
    
    this.art57Section.showSuccessTooltipMessage('Validating...');
    const validClient = this.validateClientSide(vldOptions);

    if (validClient.ok && vldOptions.validateOnServer) {
        this.onValidateServerSide();
    }
  }

  /* Client Side validation */
  private validateClientSide(validateOptions: ValidateOptions): ValidationResult {
    
    const fileSelected = validateOptions?.file ?? this.selectedFile;

    const invalid = (message: string, title = ''): ValidationResult => {
      this.postSection.setValidation( { ok: false, message: message, title: title } );
      this.art57Section.showErrorTooltipMessage(this.postSection.validationResult.message, this.postSection.validationResult.title);
      return { ok: false, message, title };
    };

    if (!fileSelected) {
      return invalid('No file selected');
    }

    const extension = fileSelected.name.split('.').pop()?.toUpperCase();
    if (extension !== 'ZIP') {
      return invalid(`Selected File's type ("${extension}") doesn't match the required one ("ZIP")`);
    }

    if (fileSelected.size > this.maxZipFileByteSize) {
      return invalid(
        `Invalid ZIP File: The size of the selected file is greater than ${Util.formatFileSize(this.maxZipFileByteSize, 0)}`
      );
    }

    this.postSection.setValidation( {ok: true, message: '', title: ''} );
    return { ok: true };
  }

  /* Button action dispatcher (receives { action, param }) */
  onButtonAction(event: any) {
    let payload = event;
    if (typeof event === 'string') { payload = { action: event, param: null }; }
    if (!payload || !payload.action) {
      this.logger.warn('Not defined action: ' + (payload?.action ?? 'undefined'));
      return;
    }

    switch (payload.action) {
      case 'resetApplication':
        this.onResetApplication();
        break;
      case 'resetSection':
        this.onResetSection();
        break;
      case 'send':
        this.onSend();
        break;
      default:
        this.logger.warn('Unhandled action: ' + payload.action);
        break;
    }

    this.buttonAction.emit(payload);
  }

  /* Reset application */
  onResetApplication() {
    this.art57Section.resetApplication().then(applicationReseted => {
      if (applicationReseted) {
        this.initComponent();
      }
    });
  }

  /* Reset section */
  onResetSection() {
    this.art57Section.resetSection(this.postSection).then(sectionReseted => {
      if (sectionReseted) {
        this.initComponent();
      }
    });
  }

  /* Show File Transfered dialog */
  private showDialogTransferCompleted(url: string, result: any, link: HTMLAnchorElement): void {
    this.confirmationService.confirm({
      header: 'File Transfer Complete',
      message: `Click on the <b>Download</b> button for the file.<br><br>Name: ${result.filename}<br><br>Posted Message<br>Message Number: ${result.messageNumber}<br>Message Receiver: ${result.receiverId}`,
      icon: 'pi pi-question-circle',
      acceptLabel: 'Download',
      rejectLabel: 'Close',
      accept: () => {
        link.click();
        URL.revokeObjectURL(url);
        this.art57Section.showSuccessTooltipMessage('Please see the downloaded file in the Browser download history');
      },
      reject: () => {
        URL.revokeObjectURL(url);
      }
    });
  }

  /* Format Bytes to KB or MB */
  formatFileSize(bytes: number | null | undefined, decimals = 2): string {
    return Util.formatFileSize(bytes, decimals);
  }

  public createDownloadLink(blob: Blob) {
    return URL.createObjectURL(blob);
  }

  private setDownloadLink(link: HTMLAnchorElement, url: any, filename: any): void {
    link.href = url;
    link.download = filename;
  }

  private createBlob(file: any): Blob {
    const binaryString = atob(file);
    const binaryData = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      binaryData[i] = binaryString.charCodeAt(i);
    }
    return new Blob([binaryData], { type: 'application/zip' });
  }

  private buildErrorTitle = (result: any): string => ''

  private buildErrorMessage = (result: any): string =>
    (this.buildErrorTitle(result) ? `\n${result.errorMessage}` : (result?.errorMessage ?? ''));

}